/**
 * Single-point Leaflet preview for a customer's last-known coordinates.
 * Uses OSM tiles and the same default marker icon fix as other BI maps.
 */
import { useEffect } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

function InvalidateSize() {
  const map = useMap();
  useEffect(() => {
    const id = window.setTimeout(() => map.invalidateSize(), 0);
    return () => window.clearTimeout(id);
  }, [map]);
  return null;
}

export interface LastKnownLocationMapProps {
  latitude: number;
  longitude: number;
  /** Shown in marker popup when set. */
  address?: string | null;
  className?: string;
  height?: number | string;
}

export function LastKnownLocationMap({
  latitude,
  longitude,
  address,
  className,
  height = 176,
}: LastKnownLocationMapProps) {
  const position: L.LatLngTuple = [latitude, longitude];

  return (
    <div className={className} style={{ height, position: 'relative', isolation: 'isolate', zIndex: 0 }}>
      <MapContainer
        key={`${latitude},${longitude}`}
        center={position}
        zoom={16}
        scrollWheelZoom={false}
        dragging
        style={{ height: '100%', width: '100%', borderRadius: 'inherit', position: 'relative', zIndex: 0 }}
      >
        <InvalidateSize />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={position}>
          {address ? (
            <Popup>
              <div className="max-w-[220px] text-xs text-slate-800">{address}</div>
            </Popup>
          ) : null}
        </Marker>
      </MapContainer>
    </div>
  );
}
